import { useMediaQuery, Box, Typography, Button, Modal } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import CharityModal from 'components/charityModal/charityModal';
import DynamicGoogleIcon from 'components/dynamic-icon/dynamicGoogleIcon';
import ImpactCarousel from 'components/impactCarousel';
import { routes } from 'routes/routes';
import { getAnonymousStripePaymentEmbedded } from 'store/auth/auth.services';
import { authState, createUserSession } from 'store/auth/auth.slice';
import { causesState, getSpecialCauseData } from 'store/causes/causes.slice';
import { CauseType, CharityType } from 'store/causes/types';
import { getImpactReportsByCharitiesIds } from 'store/charities/charities.services';
import { storeImpactReports } from 'store/charities/charities.slice';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { newFlow1State, setDonationAmount } from 'store/newFlow1/newFlow1.slice';
import { NEW_FLOW1_MAX_CONTENT_WIDTH_WITH_PADDINGS } from 'styles/constants';
import { tryParseInt, getImactCarouselData, getCalculatedAmount } from 'utils/utils';
import useClearStores from 'hooks/useClearStores';

const DynamicCausePage = () => {
   const { uniqeTerm } = useParams();
   const { donationAmount } = useAppSelector(newFlow1State);
   const { signupData } = useAppSelector(authState);
   const { clearOnLanding } = useClearStores();
   const minAmount = tryParseInt(process.env.REACT_APP_MIN_AMOUNT, 5);
   const changeAmount = tryParseInt(process.env.REACT_APP_CHANGE_AMOUNT, 5);
   const maxAmount = tryParseInt(process.env.REACT_APP_MAX_AMOUNT, 3000);
   const initialAmount = tryParseInt(process.env.REACT_APP_DEFAULT_AMOUNT, 10);
   const [isImageLoaded, setIsImageLoaded] = useState(false);

   const [causes, setCauses] = useState<string[]>([]);
   const [showImpactInfo, setShowImpactInfo] = useState(false);
   const [charityModalData, setCharityModalData] = useState<CharityType | null>(null);

   const { t } = useTranslation();
   const navigate = useNavigate();
   const dispatch = useAppDispatch();
   const { specialCauseData } = useAppSelector(causesState);

   const currentSpecialCauseData = uniqeTerm ? specialCauseData?.[uniqeTerm] : null;
   const currentCause = currentSpecialCauseData?.causes?.[0] ?? ({} as CauseType);
   const { causeHeroImage, causeHeadline, colorSVG, causeSubline, title, icon, description } = currentCause;

   const impactCarouselData = getImactCarouselData(currentSpecialCauseData?.charities ?? []);

   const feedbackTexts = [
      'getStarted.summary.feedback1',
      'getStarted.summary.feedback2',
      'getStarted.summary.feedback3',
   ];

   const prepareImpactReports = async (ids: number[]) => {
      const charitiesIds = ids.join(',');
      const response = await getImpactReportsByCharitiesIds({ charitiesIds });
      dispatch(storeImpactReports(response.data));
   };

   const handleNext = async () => {
      const metaData = {
         sytemSessionId: signupData.sessionId,
         flow: `specialCause=${uniqeTerm}`,
         ref_id: signupData.referralCode,
      };
      const cs = await getAnonymousStripePaymentEmbedded({
         money: donationAmount,
         serviceFee: 0,
         extraParam: `flow=specialCause-${uniqeTerm}`,
         metaData,
      });
      navigate(routes().dynamicCause.payment, { state: { cs } });
   };

   const changeAmountButtonClick = (increase: boolean) => {
      let multi = Math.floor(donationAmount / changeAmount);
      // eslint-disable-next-line no-plusplus
      increase ? multi++ : multi--;
      let finalValue = multi * changeAmount;
      if (finalValue < minAmount) {
         finalValue = minAmount;
      } else if (finalValue > maxAmount) {
         finalValue = maxAmount;
      }
      dispatch(setDonationAmount(finalValue));
   };

   useEffect(() => {
      if (uniqeTerm && !specialCauseData?.[uniqeTerm]) {
         clearOnLanding();
         dispatch(createUserSession());
         dispatch(getSpecialCauseData({ causeName: uniqeTerm }));
      }
      dispatch(setDonationAmount(initialAmount));
   }, [uniqeTerm]);

   useEffect(() => {
      if (currentSpecialCauseData?.causes?.[0]?.title && !causes.includes(currentSpecialCauseData.causes[0].title)) {
         setCauses((prev) => [...prev, currentSpecialCauseData.causes[0].title]);
      }
   }, [currentSpecialCauseData?.causes]);

   //  preload images
   useEffect(() => {
      if (currentSpecialCauseData?.charities && currentSpecialCauseData.charities.length > 0) {
         const charitiesIdsArray: number[] = [];
         currentSpecialCauseData.charities.forEach((charity) => {
            new Image().src = charity.image;
            charitiesIdsArray.push(charity.id);
         });
         prepareImpactReports(charitiesIdsArray);
      }
   }, [currentSpecialCauseData?.charities]);

   useEffect(() => {
      if (!causeHeroImage) return;
      const img = new Image();
      img.src = causeHeroImage;
      img.onload = () => setIsImageLoaded(true);
   }, [causeHeroImage]);

   return !isImageLoaded ? (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
         <svg width={40} height={40} viewBox='0 0 40 40'>
            <circle
               cx={20}
               cy={20}
               fill='none'
               r={18}
               strokeWidth={4}
               stroke='#F55D66'
               strokeDasharray={40}
               strokeLinecap='round'
               vectorEffect='non-scaling-stroke'
            >
               <animateTransform
                  attributeName='transform'
                  begin='0s'
                  dur='1s'
                  type='rotate'
                  from='0 20 20'
                  to='360 20 20'
                  repeatCount='indefinite'
               />
            </circle>
         </svg>
      </Box>
   ) : (
      <>
         <Box
            sx={{
               position: 'fixed',
               top: 0,
               height: '100dvh',
               left: 0,
               width: '100vw',
               backgroundImage: causeHeroImage ? `url(${causeHeroImage})` : causeHeroImage,
               backgroundSize: 'cover',
               backgroundPosition: { xs: 'left', sm: 'top' },
               zIndex: 1,
            }}
         />
         <Box
            sx={{
               flex: '1',
               display: 'flex',
               flexDirection: 'column',
               px: { xs: 2.5, md: 0 },
               pb: 5,
               width: '100%',
            }}
         >
            <Box
               sx={{
                  pt: { xs: 5, md: 5 },
                  zIndex: 10,
               }}
            >
               <Box
                  sx={{
                     padding: 2.5,
                     borderRadius: '8px',
                     width: '100%',
                     display: 'flex',
                     flexDirection: 'column',
                     backgroundColor: '#00000033',
                     backdropFilter: 'blur(4px)',
                     WebkitBackdropFilter: 'blur(4px)',
                  }}
               >
                  <Box sx={{ width: '60px', height: '60px' }}>
                     {colorSVG && (
                        <ReactSVG
                           src={`data:image/svg+xml;utf8,${encodeURIComponent(colorSVG)}`}
                           style={{ width: '60px', height: '60px' }}
                        />
                     )}
                  </Box>
                  <Typography sx={{ fontSize: '32px', lineHeight: '32px', color: '#FFFFFF', fontWeight: 700, my: 2 }}>
                     {causeHeadline}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', lineHeight: '17px', color: '#FFFFFF', fontWeight: 600, mb: 2 }}>
                     With a donation of $10/month you can help and make an annual projected impact of:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: '6px', mb: 2 }}>
                     {impactCarouselData.map((ch) => (
                        <Box
                           sx={{
                              p: 1.25,
                              borderRadius: '4px',
                              backgroundColor: '#FFFFFF',
                              width: '33%',
                              // maxHeight: '84px',
                              display: 'flex',
                              flexDirection: 'column',
                           }}
                           key={ch.id}
                        >
                           <DynamicGoogleIcon iconSizeVariant='small' iconName={ch.indicatorIcon} />
                           <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 700, color: '#333333' }}>
                              {getCalculatedAmount(10, Number(ch.indicatorPrice))}
                           </Typography>
                           <Typography sx={{ fontSize: '10px', lineHeight: '12px', fontWeight: 600, color: '#999999' }}>
                              {ch.indicatorTitle}
                           </Typography>
                        </Box>
                     ))}
                  </Box>
                  <Typography sx={{ fontSize: '14px', lineHeight: '17px', color: '#FFFFFF', fontWeight: 500, mb: 2 }}>
                     {causeSubline}
                  </Typography>
                  <Button onClick={handleNext} variant='primaryBtn'>
                     <Typography>{t('seaspiracy.intro.mainCTA')}</Typography>
                  </Button>
               </Box>
               <Box sx={{ mt: 2.5, mb: 2.5, display: 'flex', justifyContent: 'center' }}>
                  <svg width={28} height={49} viewBox='0 0 28 49' fill='none' xmlns='http://www.w3.org/2000/svg'>
                     <path
                        d='M13.994 48.783a1.5 1.5 0 0 1-.544-.1 1.5 1.5 0 0 1-.5-.35l-11.9-11.9q-.45-.45-.45-1.06t.45-1.04q.44-.45 1.045-.45t1.055.45l9.35 9.35v-13.8q0-.637.434-1.07a1.46 1.46 0 0 1 1.075-.43q.641 0 1.066.43.425.433.425 1.07v13.8l9.35-9.35q.45-.45 1.055-.45t1.045.45q.45.45.45 1.05t-.45 1.05l-11.9 11.9a1.5 1.5 0 0 1-.506.35q-.258.1-.55.1m-.003-23.4q-.641 0-1.066-.432a1.46 1.46 0 0 1-.425-1.068v-4q0-.637.434-1.07a1.46 1.46 0 0 1 1.075-.43q.641 0 1.066.43.425.433.425 1.07v4.05q0 .65-.434 1.05-.433.4-1.075.4m0-10q-.641 0-1.066-.432a1.46 1.46 0 0 1-.425-1.068v-3q0-.637.434-1.07a1.46 1.46 0 0 1 1.075-.43q.641 0 1.066.43.425.432.425 1.07v3.05q0 .65-.434 1.05-.433.4-1.075.4m-1.066-9.432q.425.432 1.066.432.642 0 1.075-.4.434-.4.434-1.05v-3.05a1.463 1.463 0 0 0-1.491-1.5q-.642 0-1.075.43-.434.432-.434 1.07v3q0 .638.425 1.068'
                        fill='#fff'
                     />
                  </svg>
               </Box>

               {/* First island */}
               <Box
                  sx={{
                     backgroundColor: '#FFFFFF',
                     py: 2.5,
                     borderRadius: '8px',
                  }}
               >
                  <Box sx={{ mb: 0.75, mx: 2.5 }}>
                     <Typography sx={{ color: '#666666', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}>
                        {t('seaspiracy.intro.donationPlan').toUpperCase()}
                     </Typography>
                  </Box>

                  <Box sx={{ mt: 0.5, mx: 2.5, pb: 1.5, mb: 1.5, borderBottom: '1px solid #E5E5E5' }}>
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        {t('seaspiracy.donationPlan.title')}
                     </Typography>
                  </Box>
                  <Box sx={{ mb: 0.75, mx: 2.5 }}>
                     <Typography sx={{ color: '#666666', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}>
                        {t('seaspiracy.intro.causes').toUpperCase()}
                     </Typography>
                  </Box>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        columnGap: '6px',
                        borderBottom: '1px solid #E5E5E5',
                        pb: 1.5,
                        mb: 1.5,
                        mx: 2.5,
                     }}
                  >
                     {causes?.map((c, idx) => (
                        <Box
                           sx={{ width: 'fit', borderRadius: '6px', backgroundColor: '#EFCAC9', py: 0.75, px: 1 }}
                           key={`${c}-${idx + 1}`}
                        >
                           <Typography sx={{ fontWeight: 600, fontSize: '12px', lineHeight: '14px' }}>{c}</Typography>
                        </Box>
                     ))}
                  </Box>

                  <Box sx={{ mb: 0.5, mx: 2.5 }}>
                     <Typography sx={{ color: '#666666', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}>
                        {t('seaspiracy.intro.charities').toUpperCase()}
                     </Typography>
                  </Box>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '6px',
                        borderBottom: '1px solid #E5E5E5',
                        mx: 2.5,
                        pb: 1.5,
                        mb: 1.5,
                     }}
                  >
                     {currentSpecialCauseData?.charities.map((charity: CharityType) => (
                        <Box
                           key={`ch-${charity.id}`}
                           sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              flexDirection: 'row',
                              columnGap: 1,
                              height: '40px',
                           }}
                        >
                           <Box
                              sx={{
                                 width: '40px',
                                 height: '40px',
                                 boxSizing: 'border-box',
                                 border: '1px solid #E6E6E6',
                                 overflow: 'hidden',
                                 borderRadius: '6px',
                              }}
                           >
                              <img alt={charity.title} src={charity.icon} height='100%' width='100%' />
                           </Box>

                           <Box
                              sx={{
                                 mr: 'auto',
                              }}
                           >
                              <Typography sx={{ fontSize: '12px', lineHeight: '14px', fontWeight: 600 }}>
                                 {charity.title}
                              </Typography>
                              <Typography
                                 noWrap
                                 sx={{
                                    fontSize: '12px',
                                    lineHeight: '13px',
                                    fontWeight: 500,
                                    color: '#999999',
                                 }}
                              >
                                 {charity.shortDescription}
                              </Typography>
                           </Box>

                           <Box onClick={() => setCharityModalData(charity)} sx={{ ml: 'auto', cursor: 'pointer' }}>
                              <DynamicGoogleIcon iconName='info' />
                           </Box>
                        </Box>
                     ))}
                  </Box>
                  <Box
                     sx={{
                        borderBottom: '1px solid #E5E5E5',
                        mx: 2.5,
                        pb: 1.5,
                        mb: 1.5,
                     }}
                  >
                     <Box sx={{ mb: 0.75 }}>
                        <Typography sx={{ color: '#666666', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}>
                           {t('seaspiracy.intro.monthlyBudget').toUpperCase()}
                        </Typography>
                     </Box>

                     <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', columnGap: 1.25 }}>
                        <Box
                           onClick={() => {
                              analytics?.track('new_flow1_amount_change', {
                                 action: 'minus',
                                 amount: donationAmount - changeAmount,
                              });
                              changeAmountButtonClick(false);
                           }}
                           sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: '#000000',
                              opacity: donationAmount === minAmount ? 0.6 : 1,
                              cursor: 'pointer',
                           }}
                        >
                           <DynamicGoogleIcon iconColor='#FFFFFF' iconName='remove' />
                        </Box>
                        <Box
                           sx={{
                              py: 0.5,
                              px: 1.5,
                              width: 85,
                              borderRadius: '4px',
                              border: '1px solid #E5E5E5',
                           }}
                        >
                           <Typography
                              textAlign='center'
                              sx={{ fontSize: '20px', lineHeight: '22px', fontWeight: 700 }}
                           >
                              ${donationAmount}
                           </Typography>
                        </Box>
                        <Box
                           onClick={() => {
                              analytics?.track('new_flow1_amount_change', {
                                 action: 'plus',
                                 amount: donationAmount + changeAmount,
                              });

                              changeAmountButtonClick(true);
                           }}
                           sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: '#000000',
                              opacity: donationAmount === maxAmount ? 0.6 : 1,
                              cursor: 'pointer',
                           }}
                        >
                           <DynamicGoogleIcon iconColor='#FFFFFF' iconName='add' />
                        </Box>
                     </Box>
                  </Box>
                  <Box>
                     <Box
                        onClick={() => setShowImpactInfo(true)}
                        sx={{
                           display: 'flex',
                           flexDirection: 'row',
                           alignItems: 'center',
                           mx: 2.5,
                           mb: 0.75,
                           cursor: 'pointer',
                        }}
                     >
                        <Typography sx={{ color: '#666666', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}>
                           {t('getStarted.summary.impact')}
                        </Typography>
                        <DynamicGoogleIcon iconColor='#666666' iconSizeVariant='small' iconName='contact_support' />
                     </Box>
                     <ImpactCarousel size='small' amount={donationAmount} charities={impactCarouselData} />
                     <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '4px', px: 2.5 }}>
                        {[1, 2, 3].map((idx) => (
                           <Typography
                              sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px' }}
                              key={`donationDot-${idx}`}
                           >
                              {t(`seaspiracy.intro.donationDot${idx}`)}
                           </Typography>
                        ))}
                     </Box>
                  </Box>
                  <Box sx={{ mt: 2.5, px: 2.5, display: 'flex', justifyContent: 'center' }}>
                     <Button onClick={handleNext} variant='primaryBtn'>
                        <Typography>{t('seaspiracy.intro.mainCTA')}</Typography>
                     </Button>
                  </Box>
               </Box>

               {/* 2nd island */}
               <Box
                  sx={{
                     backgroundColor: '#FFFFFF',
                     py: 2.5,
                     px: 2,
                     borderRadius: '8px',
                     mt: 2.5,
                     display: 'flex',
                     flexDirection: 'column',
                     rowGap: 2.5,
                  }}
               >
                  <Box>
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        {t('seaspiracy.intro.island2.title')}
                     </Typography>
                  </Box>

                  <Box>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        <Trans
                           i18nKey='seaspiracy.intro.island2.text'
                           components={{ bold: <span style={{ fontWeight: 700 }} /> }}
                        />
                     </Typography>
                  </Box>

                  <Box>
                     <Typography
                        sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px', mb: 2 }}
                     >
                        {t('seaspiracy.intro.island2.title2')}
                     </Typography>
                     <Box
                        sx={{
                           mt: 1.25,
                           display: 'flex',
                           flexDirection: 'column',
                           rowGap: 2,
                        }}
                     >
                        {currentSpecialCauseData?.charities.map((ch: CharityType, idx: number) => (
                           <React.Fragment key={`meet-${ch.title}`}>
                              {idx !== 0 && <Box sx={{ height: '1px', backgroundColor: '#E5E5E5' }} />}
                              <Box
                                 key={`meet-${ch.title}`}
                                 sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'start',
                                    columnGap: 1,
                                    overflow: 'hidden',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       borderRadius: '6px',
                                       minWidth: '40px',
                                       width: '40px',
                                       height: '40px',
                                       boxSizing: 'border-box',
                                       border: '1px solid #E6E6E6',
                                       overflow: 'hidden',
                                    }}
                                 >
                                    <img alt={ch.title} src={ch.icon} height='100%' width='100%' />
                                 </Box>

                                 <Box
                                    sx={{
                                       mr: 'auto',
                                       display: 'flex',
                                       flexDirection: 'column',
                                       justifyContent: 'start',
                                    }}
                                 >
                                    <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                                       {ch.title}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize: '14px',
                                          lineHeight: '17px',
                                          fontWeight: 500,
                                          color: '#999999',
                                       }}
                                    >
                                       {ch.description}
                                    </Typography>
                                 </Box>
                              </Box>
                           </React.Fragment>
                        ))}
                     </Box>
                  </Box>
               </Box>

               {/* 3rd island */}
               <Box
                  sx={{
                     backgroundColor: '#FFFFFF',
                     py: 2.5,
                     px: 2,
                     borderRadius: '8px',
                     mt: 2.5,
                     display: 'flex',
                     flexDirection: 'column',
                     rowGap: 2.5,
                  }}
               >
                  <Box>
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        Why {title} matters
                     </Typography>
                  </Box>

                  <Box
                     sx={{
                        width: '100%',
                        maxHeight: '240px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: '6px',
                     }}
                  >
                     <img width='100%' src={icon} alt='seaspiracy' />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        {description}
                     </Typography>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        <Trans
                           i18nKey='dynamicCause.intro.island3.text'
                           components={{ bold: <span style={{ fontWeight: 700 }} /> }}
                        />
                     </Typography>
                  </Box>
               </Box>

               {/* 5th island */}
               <Box
                  sx={{
                     backgroundColor: '#FFFFFF',
                     py: 2.5,
                     px: 2,
                     borderRadius: '8px',
                     mt: 2.5,
                     display: 'flex',
                     flexDirection: 'column',
                     rowGap: 2.5,
                  }}
               >
                  <Box>
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        {t('seaspiracy.intro.island5.title')}
                     </Typography>
                  </Box>
                  <Box>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        <Trans
                           i18nKey='seaspiracy.intro.island5.text'
                           components={{ bold: <span style={{ fontWeight: 700 }} /> }}
                        />
                     </Typography>
                  </Box>
                  <Box>
                     <Typography
                        sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px', mb: '10px' }}
                     >
                        {t('seaspiracy.intro.island5.title2')}
                     </Typography>
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           rowGap: 1,
                        }}
                     >
                        {[1, 2, 3, 4].map((dot, idx) => (
                           <Box
                              key={`dot-${dot + idx}`}
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 columnGap: '12px',
                                 alignItems: 'center',
                              }}
                           >
                              <Box
                                 sx={{
                                    minWidth: '24px',
                                    minHeight: '24px',
                                    backgroundColor: '#F55D66',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                 }}
                              >
                                 <Typography
                                    sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '12px', lineHeight: '14px' }}
                                 >
                                    {dot}
                                 </Typography>
                              </Box>
                              <Box>
                                 <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px' }}>
                                    {t(`seaspiracy.intro.island5.dot${idx + 1}`)}
                                 </Typography>
                              </Box>
                           </Box>
                        ))}
                     </Box>
                  </Box>
               </Box>

               <Box
                  sx={{
                     px: 2,
                     mt: 2.5,
                     py: 2.5,
                     borderRadius: '8px',
                     backgroundColor: '#FFFFFF',
                     display: 'flex',
                     flexDirection: 'column',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        columnGap: 0.5,
                        alignItems: 'center',
                        mb: 2.5,
                     }}
                  >
                     <DynamicGoogleIcon iconColor='#F55D66' iconName='reviews' />
                     <Typography sx={{ fontWeight: 700, fontSize: '20px', lineHeight: '22px', color: '#F55D66' }}>
                        {t('getStarted.summary.feedBackTitle')}
                     </Typography>
                  </Box>

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        columnGap: 1.25,
                        alignItems: 'stretch',
                     }}
                  >
                     {feedbackTexts.map((text) => (
                        <Box
                           sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              rowGap: 0.5,
                              px: 1.25,
                              py: 2.5,
                              borderRadius: '8px',
                              border: '1px solid #E5E5E5',
                           }}
                           key={text}
                        >
                           <DynamicGoogleIcon iconColor='#F55D66' iconName='task_alt' />
                           <Typography
                              sx={{ fontSize: '14px', lineHeight: '17px', color: '#333333' }}
                              textAlign='center'
                           >
                              {t(text)}
                           </Typography>
                        </Box>
                     ))}
                  </Box>
               </Box>

               {/* 6th island */}
               <Box
                  sx={{
                     backgroundColor: '#FFFFFF',
                     py: 2.5,
                     px: 2,
                     borderRadius: '8px',
                     mt: 2.5,
                     display: 'flex',
                     flexDirection: 'column',
                     rowGap: 2.5,
                  }}
               >
                  <Box>
                     {/* dynamicCause.intro.island3.text */}
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        {t('dynamicCause.intro.bonoMovement.title')}
                     </Typography>
                  </Box>

                  <Box>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        <Trans
                           i18nKey='dynamicCause.intro.bonoMovement.text'
                           components={{ bold: <span style={{ fontWeight: 700 }} /> }}
                        />
                     </Typography>
                  </Box>

                  <Box>
                     <Typography sx={{ color: '#333333', fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                        {t('seaspiracy.intro.island6.title2')}
                     </Typography>
                  </Box>

                  <Box>
                     <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '14px', lineHeight: '17px' }}>
                        <Trans
                           i18nKey='dynamicCause.intro.bonoMovement.text2'
                           components={{ bold: <span style={{ fontWeight: 700 }} /> }}
                        />
                     </Typography>
                  </Box>

                  <Box>
                     <Button onClick={handleNext} variant='primaryBtn'>
                        <Typography>{t('seaspiracy.intro.donateNowCTA')}</Typography>
                     </Button>
                  </Box>
               </Box>
            </Box>
         </Box>
         <Modal open={showImpactInfo} onClose={() => setShowImpactInfo(false)}>
            <Box
               sx={{
                  width: '100vw',
                  height: '100vh',
                  overflow: 'hidden',
                  position: 'absolute',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  px: 2.5,
                  '&:focus': {
                     outline: 'none',
                  },
                  '&:focus-visible': {
                     outline: 'none',
                  },
               }}
            >
               <Box
                  sx={{
                     borderRadius: '6px',
                     backgroundColor: '#FFFFFF',
                     p: 2.5,
                  }}
               >
                  <Box
                     sx={{
                        maxWidth: NEW_FLOW1_MAX_CONTENT_WIDTH_WITH_PADDINGS,
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: 2.5,
                     }}
                  >
                     <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '20px', lineHeight: '22px' }}>
                           {t('getStarted.summary.ImactInfoTitle')}
                        </Typography>
                     </Box>
                     <Box>
                        <Typography sx={{ color: '#666666', fontWeight: 600, fontSize: '16px', lineHeight: '19px' }}>
                           {t('getStarted.summary.ImactInfoText')}
                        </Typography>
                     </Box>
                     <Box>
                        <Button onClick={() => setShowImpactInfo(false)} variant='primaryBtn'>
                           <Typography>{t('getStarted.summary.ImpactInfoCTA')}</Typography>
                        </Button>
                     </Box>
                  </Box>
               </Box>
            </Box>
         </Modal>
         <CharityModal charityModalData={charityModalData} handleClose={() => setCharityModalData(null)} />
      </>
   );
};

export default DynamicCausePage;
